import { useState } from "react";
import { IconCheck, IconX } from "./Icons";
import { Product } from "../data/product";
import { useLanguage } from "../context/LanguageContext";
import { uiText } from "../i18n/ui";

interface CheckoutModalProps {
  product: Product;
  courseId: string;
  onClose: () => void;
}

interface FormState {
  fullName: string;
  surname: string;
  email: string;
  phone: string;
  identityNumber: string;
  billingType: "individual" | "corporate";
  companyName: string;
  taxOffice: string;
  taxNumber: string;
  address: string;
  city: string;
  district: string;
  zipCode: string;
  cardHolderName: string;
  cardNumber: string;
  expireMonth: string;
  expireYear: string;
  cvv: string;
  installmentCount: number;
}

const initialFormState: FormState = {
  fullName: "",
  surname: "",
  email: "",
  phone: "",
  identityNumber: "",
  billingType: "individual",
  companyName: "",
  taxOffice: "",
  taxNumber: "",
  address: "",
  city: "",
  district: "",
  zipCode: "",
  cardHolderName: "",
  cardNumber: "",
  expireMonth: "",
  expireYear: "",
  cvv: "",
  installmentCount: 1,
};

function decodeBase64Html(base64: string): string {
  try {
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch {
    return "";
  }
}

// Normalizes a 2-digit expiry year ("31") to 4 digits (2031) for the current century.
function toFourDigitYear(year: string): number {
  const digits = year.trim();
  if (digits.length <= 2) {
    return 2000 + Number(digits);
  }
  return Number(digits);
}

export default function CheckoutModal({ product, courseId, onClose }: CheckoutModalProps) {
  const { lang } = useLanguage();
  const t = uiText[lang].checkout;
  const currencyFormatter = new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-US", {
    style: "currency",
    currency: product.currency,
    maximumFractionDigits: 0,
  });

  const [form, setForm] = useState<FormState>(initialFormState);
  const [paymentType, setPaymentType] = useState<"remaining" | "deposit">("deposit");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [threeDsHtml, setThreeDsHtml] = useState<string | null>(null);

  const amount = paymentType === "deposit" ? product.depositAmount : product.remainingAmount;

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePaymentTypeChange = (type: "remaining" | "deposit") => {
    setPaymentType(type);
    if (type === "deposit") {
      updateField("installmentCount", 1);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);

    if (
      !form.fullName.trim() ||
      !form.surname.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.address.trim() ||
      !form.city.trim() ||
      !form.cardHolderName.trim() ||
      !form.cardNumber.trim() ||
      !form.expireMonth ||
      !form.expireYear ||
      !form.cvv.trim()
    ) {
      setErrorMessage(t.requiredField);
      return;
    }

    if (form.billingType === "corporate" && (!form.companyName.trim() || !form.taxNumber.trim())) {
      setErrorMessage(t.requiredField);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("/.netlify/functions/tami-init-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount,
          courseId,
          installmentCount: paymentType === "deposit" ? 1 : form.installmentCount,
          basketItemName:
            paymentType === "deposit" ? `${product.shortName} — ${t.depositPaymentLabel}` : product.shortName,
          buyer: {
            name: form.fullName,
            surName: form.surname,
            emailAddress: form.email,
            phoneNumber: form.phone,
            identityNumber: form.identityNumber || undefined,
          },
          billingAddress: {
            address: form.address,
            city: form.city,
            district: form.district,
            zipCode: form.zipCode,
            country: "Türkiye",
            companyName: form.billingType === "corporate" ? form.companyName : undefined,
            taxOffice: form.billingType === "corporate" ? form.taxOffice : undefined,
            taxNumber: form.billingType === "corporate" ? form.taxNumber : undefined,
          },
          card: {
            holderName: form.cardHolderName,
            number: form.cardNumber.replace(/\s+/g, ""),
            expireMonth: Number(form.expireMonth),
            expireYear: toFourDigitYear(form.expireYear),
            cvv: form.cvv,
          },
        }),
      });

      const data = await response.json();

      if (!data.success || !data.threeDSHtmlContent) {
        setErrorMessage(data.errorMessage || t.genericError);
        setSubmitting(false);
        return;
      }

      const html = decodeBase64Html(data.threeDSHtmlContent);
      if (!html) {
        setErrorMessage(t.genericError);
        setSubmitting(false);
        return;
      }
      setThreeDsHtml(html);
    } catch {
      setErrorMessage(t.genericError);
      setSubmitting(false);
    }
  };

  if (threeDsHtml) {
    const handleCancel3ds = () => {
      setThreeDsHtml(null);
      setSubmitting(false);
      onClose();
    };

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={handleCancel3ds} aria-hidden="true" />
        <div className="relative w-full max-w-lg sm:max-w-xl bg-white rounded-2xl shadow-2xl p-4 sm:p-6 max-h-[90vh] overflow-hidden flex flex-col">
          <button
            onClick={handleCancel3ds}
            aria-label={t.close}
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-lg text-muted hover:bg-light hover:text-primary transition-colors z-10"
          >
            <IconX size={20} />
          </button>
          <p className="text-sm text-muted mb-3 text-center pr-8">{t.loading3ds}</p>
          <iframe
            title="3D Secure"
            srcDoc={threeDsHtml}
            className="w-full flex-1 rounded-xl border border-border"
            style={{ minHeight: "60vh" }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div className="relative w-full max-w-lg sm:max-w-xl lg:max-w-2xl bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          aria-label={t.close}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-lg text-muted hover:bg-light hover:text-primary transition-colors"
        >
          <IconX size={20} />
        </button>

        <h3 className="text-xl sm:text-2xl font-bold text-primary mb-1">{t.title}</h3>
        <p className="text-muted mb-6">
          {product.shortName} — <span className="font-semibold text-primary">{currencyFormatter.format(amount)}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">{t.sectionPaymentType}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handlePaymentTypeChange("deposit")}
                className={`rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                  paymentType === "deposit" ? "border-accent bg-accent/5" : "border-border"
                }`}
              >
                <div className="font-semibold text-primary text-sm">{t.depositPaymentLabel}</div>
                <div className="text-sm text-muted mt-0.5">{currencyFormatter.format(product.depositAmount)}</div>
              </button>
              <button
                type="button"
                onClick={() => handlePaymentTypeChange("remaining")}
                className={`rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                  paymentType === "remaining" ? "border-accent bg-accent/5" : "border-border"
                }`}
              >
                <div className="font-semibold text-primary text-sm">{t.remainingPaymentLabel}</div>
                <div className="text-sm text-muted mt-0.5">{currencyFormatter.format(product.remainingAmount)}</div>
              </button>
            </div>
            {paymentType === "deposit" ? (
              <p className="mt-2 text-xs text-muted">{t.depositNote(currencyFormatter.format(product.depositAmount))}</p>
            ) : (
              <p className="mt-2 text-xs text-muted">{t.remainingNote}</p>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">{t.sectionContact}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                required
                placeholder={t.fullName}
                value={form.fullName}
                onChange={(e) => updateField("fullName", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                required
                placeholder={t.surname}
                value={form.surname}
                onChange={(e) => updateField("surname", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                required
                type="email"
                placeholder={t.email}
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                required
                type="tel"
                placeholder={t.phone}
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                placeholder={t.identityNumber}
                value={form.identityNumber}
                onChange={(e) => updateField("identityNumber", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">{t.sectionBilling}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <button
                type="button"
                onClick={() => updateField("billingType", "individual")}
                className={`rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                  form.billingType === "individual" ? "border-accent bg-accent/5 text-primary" : "border-border text-muted"
                }`}
              >
                {t.billingTypeIndividual}
              </button>
              <button
                type="button"
                onClick={() => updateField("billingType", "corporate")}
                className={`rounded-xl border-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                  form.billingType === "corporate" ? "border-accent bg-accent/5 text-primary" : "border-border text-muted"
                }`}
              >
                {t.billingTypeCorporate}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.billingType === "corporate" && (
                <>
                  <input
                    required
                    placeholder={t.companyName}
                    value={form.companyName}
                    onChange={(e) => updateField("companyName", e.target.value)}
                    className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
                  />
                  <input
                    placeholder={t.taxOffice}
                    value={form.taxOffice}
                    onChange={(e) => updateField("taxOffice", e.target.value)}
                    className="rounded-xl border border-border px-4 py-2.5 text-sm"
                  />
                  <input
                    required
                    placeholder={t.taxNumber}
                    value={form.taxNumber}
                    onChange={(e) => updateField("taxNumber", e.target.value)}
                    className="rounded-xl border border-border px-4 py-2.5 text-sm"
                  />
                </>
              )}
              <input
                required
                placeholder={t.address}
                value={form.address}
                onChange={(e) => updateField("address", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
              />
              <input
                required
                placeholder={t.city}
                value={form.city}
                onChange={(e) => updateField("city", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                placeholder={t.district}
                value={form.district}
                onChange={(e) => updateField("district", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
              />
              <input
                placeholder={t.zipCode}
                value={form.zipCode}
                onChange={(e) => updateField("zipCode", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-3">{t.sectionCard}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                required
                placeholder={t.cardHolderName}
                value={form.cardHolderName}
                onChange={(e) => updateField("cardHolderName", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
                autoComplete="cc-name"
              />
              <input
                required
                inputMode="numeric"
                placeholder={t.cardNumber}
                value={form.cardNumber}
                onChange={(e) => updateField("cardNumber", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
                autoComplete="cc-number"
                maxLength={19}
              />
              <input
                required
                inputMode="numeric"
                placeholder={t.expireMonth}
                value={form.expireMonth}
                onChange={(e) => updateField("expireMonth", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
                autoComplete="cc-exp-month"
                maxLength={2}
              />
              <input
                required
                inputMode="numeric"
                placeholder={t.expireYear}
                value={form.expireYear}
                onChange={(e) => updateField("expireYear", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm"
                autoComplete="cc-exp-year"
                maxLength={4}
              />
              <input
                required
                inputMode="numeric"
                placeholder={t.cvv}
                value={form.cvv}
                onChange={(e) => updateField("cvv", e.target.value)}
                className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2"
                autoComplete="cc-csc"
                maxLength={4}
              />
              {paymentType === "remaining" && (
                <select
                  value={form.installmentCount}
                  onChange={(e) => updateField("installmentCount", Number(e.target.value))}
                  className="rounded-xl border border-border px-4 py-2.5 text-sm sm:col-span-2 bg-white"
                >
                  {product.installmentOptions.map((count) => (
                    <option key={count} value={count}>
                      {count === 1 ? t.singlePayment : `${count} ${t.installmentsSuffix}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {errorMessage && (
            <p className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {errorMessage}
            </p>
          )}

          <div className="flex items-start gap-2.5 text-xs text-muted">
            <IconCheck size={14} className="flex-shrink-0 mt-0.5 text-accent" />
            <span>{t.secureNote}</span>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-light disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300"
          >
            {submitting ? t.processing : `${t.submit} — ${currencyFormatter.format(amount)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
